import { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  Play,
  RefreshCw,
  Search,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { adminApi } from "@/lib/admin-api";
import { fmtRelative, useAdminList } from "./_useAdminList";

const WINNER_TYPES = [
  { value: "any", label: "Any winnerType" },
  { value: "likely_conversion_winner", label: "likely_conversion_winner" },
  { value: "current_cohort_survivor", label: "current_cohort_survivor" },
  { value: "recent_structural_signal", label: "recent_structural_signal" },
  { value: "historical_long_runner", label: "historical_long_runner" },
];

function CreativeThumb({ ad }) {
  if (ad.videoUrl) {
    return (
      <div className="relative h-14 w-14 rounded bg-black/80 overflow-hidden flex items-center justify-center">
        {ad.imageUrl ? (
          <img src={ad.imageUrl} alt="" className="h-full w-full object-cover opacity-70" />
        ) : null}
        <Play className="absolute h-5 w-5 text-white drop-shadow" />
      </div>
    );
  }
  if (ad.imageUrl) {
    return (
      <img
        src={ad.imageUrl}
        alt=""
        className="h-14 w-14 rounded object-cover bg-muted"
        loading="lazy"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    );
  }
  return (
    <div className="h-14 w-14 rounded bg-muted flex items-center justify-center text-[10px] text-muted-foreground">
      no image
    </div>
  );
}

export default function AdminAds() {
  const [q, setQ] = useState("");
  const [storeId, setStoreId] = useState("");
  const [competitorId, setCompetitorId] = useState("");
  const [platform, setPlatform] = useState("any");
  const [status, setStatus] = useState("active");
  const [winnerType, setWinnerType] = useState("any");
  const [missingCreative, setMissingCreative] = useState(false);

  const list = useAdminList({
    fetcher: adminApi.ads,
    dataKey: "ads",
    defaultPageSize: 25,
    defaultFilters: { status: "active" },
  });

  const totalPages = Math.max(1, Math.ceil(list.total / list.pageSize));

  const apply = () =>
    list.setFilters({
      q,
      storeId: storeId ? parseInt(storeId, 10) : undefined,
      competitorId: competitorId ? parseInt(competitorId, 10) : undefined,
      platform: platform === "any" ? undefined : platform,
      status,
      winnerType: winnerType === "any" ? undefined : winnerType,
      missingCreative: missingCreative ? 1 : undefined,
    });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Ads</h1>
          <p className="text-sm text-muted-foreground">
            {list.total.toLocaleString()} ads matching the current filter.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => list.refetch()}
          disabled={list.refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${list.refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-7 gap-2">
        <div className="relative col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") apply();
            }}
            placeholder="Title / body / adId"
            className="pl-9 h-9"
          />
        </div>
        <Input
          type="number"
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
          placeholder="Store ID"
          className="h-9"
        />
        <Input
          type="number"
          value={competitorId}
          onChange={(e) => setCompetitorId(e.target.value)}
          placeholder="Competitor ID"
          className="h-9"
        />
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any platform</SelectItem>
            <SelectItem value="meta">meta</SelectItem>
            <SelectItem value="google">google</SelectItem>
            <SelectItem value="tiktok">tiktok</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">active</SelectItem>
            <SelectItem value="inactive">inactive</SelectItem>
            <SelectItem value="all">all</SelectItem>
          </SelectContent>
        </Select>
        <Select value={winnerType} onValueChange={setWinnerType}>
          <SelectTrigger className="h-9">
            <SelectValue placeholder="winnerType" />
          </SelectTrigger>
          <SelectContent>
            {WINNER_TYPES.map((wt) => (
              <SelectItem key={wt.value} value={wt.value}>{wt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={missingCreative}
            onChange={(e) => setMissingCreative(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Missing creative only
        </label>
        <Button size="sm" onClick={apply}>Apply</Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setQ("");
            setStoreId("");
            setCompetitorId("");
            setPlatform("any");
            setStatus("active");
            setWinnerType("any");
            setMissingCreative(false);
            list.setFilters({
              q: "", storeId: undefined, competitorId: undefined,
              platform: undefined, status: "active",
              winnerType: undefined, missingCreative: undefined,
            });
          }}
        >
          Clear
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {list.loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading ads…
            </div>
          ) : list.error ? (
            <div className="p-6 text-sm text-destructive">{list.error}</div>
          ) : list.data.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No ads match.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Creative</TableHead>
                  <TableHead>Brand · Title</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Layer D</TableHead>
                  <TableHead>First seen</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="text-right">Snapshot</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.map((ad) => {
                  const winnerType = ad.layerD?.winnerType;
                  const confidence = ad.layerD?.judgmentConfidence;
                  return (
                    <TableRow key={ad.id}>
                      <TableCell>
                        <CreativeThumb ad={ad} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {ad.competitor?.logoUrl && (
                            <img
                              src={ad.competitor.logoUrl}
                              alt=""
                              className="h-5 w-5 rounded object-contain bg-muted"
                            />
                          )}
                          <span className="text-xs font-medium truncate max-w-[140px]">
                            {ad.competitor?.brandName || "—"}
                          </span>
                        </div>
                        <div className="mt-1 text-xs font-medium line-clamp-1 max-w-md">
                          {ad.adCreativeTitle || ad.title || "(no title)"}
                        </div>
                        <div className="text-[11px] text-muted-foreground line-clamp-2 max-w-md">
                          {ad.adCreativeBody}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          adId={ad.adId} · id={ad.id}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs uppercase">
                          {ad.platform}
                        </Badge>
                        {!ad.isActive && (
                          <Badge variant="secondary" className="ml-1 text-[10px]">inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {winnerType ? (
                          <>
                            <Badge variant="default" className="text-[10px]">{winnerType}</Badge>
                            {confidence != null && (
                              <div className="text-[10px] text-muted-foreground mt-1">
                                conf {Number(confidence).toFixed(2)}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {fmtRelative(ad.firstSeenAt)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {fmtRelative(ad.lastSeenAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        {ad.adSnapshotUrl ? (
                          <a
                            href={ad.adSnapshotUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center text-xs text-primary hover:underline"
                          >
                            Open <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div>
          Page {list.page} of {totalPages} · {list.total.toLocaleString()} ads
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={list.page <= 1}
            onClick={() => list.setPage(list.page - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={list.page >= totalPages}
            onClick={() => list.setPage(list.page + 1)}
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
